import type {
  ActualCostSource,
  CostAttribution,
  AttributionQuery,
  CostAttributionRow,
  ParsedScopeKey,
  CostLimit,
} from '@fabric-harness/sdk';
import { parseScopeKey, tenantCostLimit } from '@fabric-harness/sdk';
import { runStatement, parseStatementRows } from './sql-exec.js';
import type { DatabricksStatementClient, SqlParameter, StatementPoll } from './sql-exec.js';

export interface DatabricksActualCostSourceOptions {
  /** Cache TTL in milliseconds. Default 60_000. */
  cacheTtlMs?: number;
  /** Tag key prefix for filtering usage rows. Default `'fabric'`. */
  tagPrefix?: string;
  /** Polling configuration for statement execution. */
  poll?: StatementPoll;
}

export interface DatabricksCostReconciliationOptions {
  scopeKey: string;
  estimatedUsd: number;
  usageOccurredAt: string | number | Date;
  attribution?: CostAttribution;
  actualSource: ActualCostSource;
  /** Billing export delay before a comparison is meaningful. Default 24 hours. */
  accountingDelayMs?: number;
  maxDriftUsd?: number;
  maxDriftPercent?: number;
  unavailable?: 'fail-open' | 'fail-closed';
  now?: () => number;
  onDrift?: (result: DatabricksCostReconciliationResult) => void | Promise<void>;
}

export interface DatabricksCostReconciliationResult {
  scopeKey: string;
  status: 'pending-accounting' | 'within-threshold' | 'drift' | 'unavailable';
  enforcement: 'allow' | 'deny';
  estimatedUsd: number;
  actualUsd?: number;
  driftUsd?: number;
  driftPercent?: number;
  actualsFetchedAt?: string;
  nextCheckAt?: string;
  error?: string;
}

/** Compare immediate estimates with delayed System Tables actuals and apply an explicit outage policy. */
export async function reconcileDatabricksCost(options: DatabricksCostReconciliationOptions): Promise<DatabricksCostReconciliationResult> {
  const now = (options.now ?? Date.now)();
  const occurredAt = new Date(options.usageOccurredAt).getTime();
  if (!Number.isFinite(occurredAt)) throw new Error('usageOccurredAt must be a valid date or epoch value.');
  const accountingDelayMs = options.accountingDelayMs ?? 24 * 60 * 60_000;
  const matureAt = occurredAt + accountingDelayMs;
  if (now < matureAt) {
    return {
      scopeKey: options.scopeKey,
      status: 'pending-accounting',
      enforcement: 'allow',
      estimatedUsd: options.estimatedUsd,
      nextCheckAt: new Date(matureAt).toISOString(),
    };
  }
  try {
    const actual = await options.actualSource.getUsd(options.scopeKey, options.attribution, { cacheTtlMs: 0 });
    const driftUsd = actual.usd - options.estimatedUsd;
    const driftPercent = options.estimatedUsd === 0
      ? actual.usd === 0 ? 0 : 100
      : (driftUsd / options.estimatedUsd) * 100;
    const drift = Math.abs(driftUsd) > (options.maxDriftUsd ?? 1) || Math.abs(driftPercent) > (options.maxDriftPercent ?? 10);
    const result: DatabricksCostReconciliationResult = {
      scopeKey: options.scopeKey,
      status: drift ? 'drift' : 'within-threshold',
      enforcement: drift && options.unavailable === 'fail-closed' ? 'deny' : 'allow',
      estimatedUsd: options.estimatedUsd,
      actualUsd: actual.usd,
      driftUsd,
      driftPercent,
      ...(actual.actualsFetchedAt ? { actualsFetchedAt: actual.actualsFetchedAt } : {}),
    };
    if (drift) await options.onDrift?.(result);
    return result;
  } catch (error) {
    const result: DatabricksCostReconciliationResult = {
      scopeKey: options.scopeKey,
      status: 'unavailable',
      enforcement: options.unavailable === 'fail-closed' ? 'deny' : 'allow',
      estimatedUsd: options.estimatedUsd,
      error: error instanceof Error ? error.message : String(error),
    };
    if (result.enforcement === 'deny') throw new Error(`Databricks actual-cost reconciliation unavailable: ${result.error}`);
    return result;
  }
}

interface CacheEntry {
  usd: number;
  dbus: number | undefined;
  fetchedAt: string;
  expiresAt: number;
}

function toNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function deriveDateWindow(parsed: ParsedScopeKey): { startDate: string; endDate: string } | undefined {
  const period = parsed.period;
  if (!period) return undefined;

  const parts = period.split(':');
  const periodType = parts[0];
  const periodValue = parts.slice(1).join(':');

  if (!periodValue) return undefined;

  switch (periodType) {
    case 'day': {
      // day:YYYY-MM-DD
      return { startDate: periodValue, endDate: periodValue };
    }
    case 'hour': {
      // hour:YYYY-MM-DDTHH:00Z → use the date portion
      const date = periodValue.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
      return date ? { startDate: date, endDate: date } : undefined;
    }
    case 'month': {
      // month:YYYY-MM
      const [year, month] = periodValue.split('-');
      if (!year || !month) return undefined;
      const lastDay = new Date(Number(year), Number(month), 0).getDate();
      return {
        startDate: `${year}-${month}-01`,
        endDate: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
      };
    }
    default:
      return undefined;
  }
}

function buildCostQuery(tagPrefix: string, attribution?: CostAttribution): string {
  const tagAgent = `${tagPrefix}.agent`;
  const tagTenant = `${tagPrefix}.tenant`;
  const tagUser = `${tagPrefix}.user`;

  const conditions: string[] = [
    'u.usage_date >= :start_date AND u.usage_date <= :end_date',
  ];

  if (attribution?.agentId) {
    conditions.push(`u.tags['${tagAgent}'] = :agent_id`);
  }
  if (attribution?.tenantId) {
    conditions.push(`u.tags['${tagTenant}'] = :tenant_id`);
  }
  if (attribution?.userId) {
    conditions.push(`u.tags['${tagUser}'] = :user_id`);
  }

  return [
    'SELECT',
    '  SUM(u.usage_quantity * lp.pricing.default) AS list_cost_usd,',
    '  SUM(u.usage_quantity) AS quantity',
    'FROM system.billing.usage u',
    'LEFT JOIN system.billing.list_prices lp',
    '  ON u.cloud = lp.cloud AND u.sku_name = lp.sku_name AND u.usage_unit = lp.usage_unit',
    ' AND u.usage_end_time >= lp.price_start_time',
    ' AND (lp.price_end_time IS NULL OR u.usage_end_time < lp.price_end_time)',
    `WHERE ${conditions.join(' AND ')}`,
  ].join('\n');
}

function buildAttributionQuery(
  tagPrefix: string,
  filters: AttributionQuery,
): { query: string; parameters: SqlParameter[] } {
  const tagAgent = `${tagPrefix}.agent`;
  const tagTenant = `${tagPrefix}.tenant`;
  const tagUser = `${tagPrefix}.user`;

  const conditions: string[] = [
    'u.usage_date >= :start_date AND u.usage_date <= :end_date',
  ];
  const parameters: SqlParameter[] = [
    { name: 'start_date', value: filters.startDate ?? '', type: 'DATE' },
    { name: 'end_date', value: filters.endDate ?? '', type: 'DATE' },
  ];

  if (filters.agentId) {
    conditions.push(`u.tags['${tagAgent}'] = :agent_id`);
    parameters.push({ name: 'agent_id', value: filters.agentId });
  }
  if (filters.tenantId) {
    conditions.push(`u.tags['${tagTenant}'] = :tenant_id`);
    parameters.push({ name: 'tenant_id', value: filters.tenantId });
  }
  if (filters.userId) {
    conditions.push(`u.tags['${tagUser}'] = :user_id`);
    parameters.push({ name: 'user_id', value: filters.userId });
  }
  if (filters.model) {
    conditions.push('u.sku_name = :model');
    parameters.push({ name: 'model', value: filters.model });
  }
  if (filters.provider) {
    conditions.push('u.cloud = :provider');
    parameters.push({ name: 'provider', value: filters.provider });
  }

  const groupBy = filters.groupBy ?? ['agentId', 'tenantId', 'userId', 'model', 'provider'];
  const groupColumns: string[] = [];
  const selectColumns: string[] = [];

  for (const dim of groupBy) {
    switch (dim) {
      case 'agentId':
        selectColumns.push(`u.tags['${tagAgent}'] AS agentId`);
        groupColumns.push(`u.tags['${tagAgent}']`);
        break;
      case 'tenantId':
        selectColumns.push(`u.tags['${tagTenant}'] AS tenantId`);
        groupColumns.push(`u.tags['${tagTenant}']`);
        break;
      case 'userId':
        selectColumns.push(`u.tags['${tagUser}'] AS userId`);
        groupColumns.push(`u.tags['${tagUser}']`);
        break;
      case 'model':
        selectColumns.push('u.sku_name AS model');
        groupColumns.push('u.sku_name');
        break;
      case 'provider':
        selectColumns.push('u.cloud AS provider');
        groupColumns.push('u.cloud');
        break;
      case 'sessionId':
        // sessionId is not stored in billing tags; skip as a group dimension
        break;
      case 'turnId':
        // turnId is not stored in billing tags; skip as a group dimension
        break;
    }
  }

  selectColumns.push(
    "CONCAT('day:', u.usage_date) AS period",
    'SUM(u.usage_quantity * lp.pricing.default) AS actualUsd',
    'SUM(u.usage_quantity) AS actualDbus',
    'COUNT(*) AS calls',
  );
  groupColumns.push("CONCAT('day:', u.usage_date)");

  const query = [
    'SELECT',
    `  ${selectColumns.join(',\n  ')}`,
    'FROM system.billing.usage u',
    'LEFT JOIN system.billing.list_prices lp',
    '  ON u.cloud = lp.cloud AND u.sku_name = lp.sku_name AND u.usage_unit = lp.usage_unit',
    ' AND u.usage_end_time >= lp.price_start_time',
    ' AND (lp.price_end_time IS NULL OR u.usage_end_time < lp.price_end_time)',
    `WHERE ${conditions.join(' AND ')}`,
    `GROUP BY ${groupColumns.join(', ')}`,
    'ORDER BY actualUsd DESC',
  ].join('\n');

  return { query, parameters };
}

/**
 * Create a Databricks-backed `ActualCostSource` that queries `system.billing.usage`
 * joined to `system.billing.list_prices` for real spend data.
 *
 * Results are cached per-scope-key for `cacheTtlMs` (default 60s) to avoid
 * hammering the SQL warehouse.
 */
export function databricksActualCostSource(
  client: DatabricksStatementClient,
  warehouseId: string,
  options: DatabricksActualCostSourceOptions = {},
): ActualCostSource {
  const cacheTtlMs = options.cacheTtlMs ?? 60_000;
  const tagPrefix = options.tagPrefix ?? 'fabric';
  const poll = options.poll ?? { intervalMs: 2000, timeoutMs: 120_000 };
  const cache = new Map<string, CacheEntry>();

  return {
    async getUsd(scopeKey, attribution, queryOptions) {
      const ttl = queryOptions?.cacheTtlMs ?? cacheTtlMs;
      const now = Date.now();

      // Check cache first
      const cached = cache.get(scopeKey);
      if (cached && cached.expiresAt > now) {
        const result: { usd: number; dbus?: number; actualsFetchedAt?: string; cacheTtlMs?: number } = {
          usd: cached.usd,
        };
        if (cached.dbus !== undefined) result.dbus = cached.dbus;
        result.actualsFetchedAt = cached.fetchedAt;
        result.cacheTtlMs = ttl;
        return result;
      }

      const parsed = parseScopeKey(scopeKey);
      if (!parsed) {
        return { usd: 0, cacheTtlMs: ttl };
      }

      const dateWindow = deriveDateWindow(parsed);
      if (!dateWindow) {
        return { usd: 0, cacheTtlMs: ttl };
      }

      const parameters: SqlParameter[] = [
        { name: 'start_date', value: dateWindow.startDate, type: 'DATE' },
        { name: 'end_date', value: dateWindow.endDate, type: 'DATE' },
      ];

      if (attribution?.agentId) {
        parameters.push({ name: 'agent_id', value: attribution.agentId });
      }
      if (attribution?.tenantId) {
        parameters.push({ name: 'tenant_id', value: attribution.tenantId });
      }
      if (attribution?.userId) {
        parameters.push({ name: 'user_id', value: attribution.userId });
      }

      const response = await runStatement(
        client,
        warehouseId,
        buildCostQuery(tagPrefix, attribution),
        parameters,
        poll,
      );

      const rows = parseStatementRows(response);
      const usd = rows.reduce((sum, r) => sum + toNumber(r.list_cost_usd), 0);
      const dbus = rows.reduce((sum, r) => sum + toNumber(r.quantity), 0);
      const fetchedAt = new Date().toISOString();

      cache.set(scopeKey, {
        usd,
        dbus: dbus || undefined,
        fetchedAt,
        expiresAt: now + ttl,
      });

      const result: { usd: number; dbus?: number; actualsFetchedAt?: string; cacheTtlMs?: number } = {
        usd,
      };
      if (dbus) result.dbus = dbus;
      result.actualsFetchedAt = fetchedAt;
      result.cacheTtlMs = ttl;
      return result;
    },

    async queryAttribution(filters) {
      if (!filters.startDate || !filters.endDate) {
        throw new Error('queryAttribution requires startDate and endDate');
      }

      const { query, parameters } = buildAttributionQuery(tagPrefix, filters);
      const response = await runStatement(client, warehouseId, query, parameters, poll);
      const rows = parseStatementRows(response);

      return rows.map((r) => {
        const row: {
          agentId?: string;
          userId?: string;
          tenantId?: string;
          model?: string;
          provider?: string;
          period: string;
          estimatedUsd: number;
          actualUsd: number;
          actualDbus: number;
          deltaUsd: number;
          calls: number;
        } = {
          period: String(r.period ?? ''),
          estimatedUsd: 0,
          actualUsd: toNumber(r.actualUsd),
          actualDbus: toNumber(r.actualDbus),
          deltaUsd: toNumber(r.actualUsd),
          calls: toNumber(r.calls),
        };
        if (r.agentId) row.agentId = String(r.agentId);
        if (r.userId) row.userId = String(r.userId);
        if (r.tenantId) row.tenantId = String(r.tenantId);
        if (r.model) row.model = String(r.model);
        if (r.provider) row.provider = String(r.provider);
        return row;
      });
    },
  };
}

export function databricksTenantCostLimit(
  client: DatabricksStatementClient,
  warehouseId: string,
  tenantId: string,
  options: {
    perHourUsd?: number;
    perDayUsd?: number;
    perMonthUsd?: number;
    cacheTtlMs?: number;
    onExceed?: 'throw' | 'approve';
  },
): CostLimit {
  const base = tenantCostLimit(tenantId, {
    ...(options.perHourUsd !== undefined ? { perHourUsd: options.perHourUsd } : {}),
    ...(options.perDayUsd !== undefined ? { perDayUsd: options.perDayUsd } : {}),
    ...(options.perMonthUsd !== undefined ? { perMonthUsd: options.perMonthUsd } : {}),
    ...(options.onExceed ? { onExceed: options.onExceed } : {}),
  });
  const opts: DatabricksActualCostSourceOptions = {};
  if (options.cacheTtlMs !== undefined) opts.cacheTtlMs = options.cacheTtlMs;
  return {
    ...base,
    scopeSource: 'external',
    actualSource: databricksActualCostSource(client, warehouseId, opts),
  };
}
